import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Stethoscope, GraduationCap,
  Home, CalendarClock, User, Phone, Building2, ClipboardList, FileText, Hash,
  Download, Image as ImageIcon, Pencil, Check,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fileUrl } from "@/components/ImageUpload";
import Logo from "@/components/Logo";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import formatDoctorName from "@/lib/names";

const STATUSES = ["Pending", "Scheduled", "Decorated"];
const STANDEE_STYLES = {
  None: "bg-slate-100 text-slate-500",
  "Image Uploaded": "bg-blue-50 text-blue-600",
  "Standee Downloaded": "bg-emerald-50 text-emerald-600",
};
const STATUS_STYLES = {
  Pending: "text-amber-600",
  Scheduled: "text-blue-600",
  Decorated: "text-emerald-600",
};

function Row({ icon, label, value, mono = false }) {
  const empty = value === undefined || value === null || String(value).trim() === "";
  return (
    <div className="py-3.5 border-b border-slate-100 last:border-0">
      <p className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400 flex items-center gap-2 mb-1.5">
        {icon} {label}
      </p>
      {empty ? (
        <p className="text-slate-300">—</p>
      ) : (
        <p className={`text-slate-800 whitespace-pre-line break-words ${mono ? "font-mono text-sm" : ""}`}>{value}</p>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.04)] overflow-hidden">
      <h2 className="px-5 sm:px-6 py-3.5 bg-slate-50 border-b border-slate-200 font-heading font-bold text-slate-900 text-sm">
        {title}
      </h2>
      <div className="px-5 sm:px-6 py-1">{children}</div>
    </section>
  );
}

function Photo({ label, path }) {
  if (!path) {
    return (
      <div>
        <p className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400 mb-2">{label}</p>
        <div className="h-48 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 grid place-items-center text-slate-300 text-sm">
          Not uploaded
        </div>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400 mb-2">{label}</p>
      <a href={fileUrl(path)} target="_blank" rel="noreferrer" title={`Open ${label} full size`}>
        <img
          src={fileUrl(path)}
          alt={label}
          className="w-full h-48 object-cover rounded-xl border border-slate-200 hover:ring-2 hover:ring-[#0B5351] transition-all"
          onError={(e) => {
            e.currentTarget.replaceWith(
              Object.assign(document.createElement("div"), {
                className: "h-48 rounded-xl border border-slate-200 bg-slate-50 grid place-items-center text-slate-400 text-sm",
                innerText: "Photo unavailable",
              }),
            );
          }}
        />
      </a>
    </div>
  );
}

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
      });
};

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ doctor_name: "", qualification: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/admin/submissions/${id}`)
      .then(({ data }) => { if (alive) setSub(data); })
      .catch((err) => {
        if (!alive) return;
        setNotFound(true);
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load submission");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const downloadStandee = () => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${id}/standee/download?auth=${token}`, "_blank");
    // Re-read so the status reflects the download the server just recorded.
    setTimeout(() => {
      api.get(`/admin/submissions/${id}`).then(({ data }) => setSub(data)).catch(() => {});
    }, 1500);
  };

  const startEdit = () => {
    // Offer the tidied spelling as the starting point — usually what they want to keep.
    setDraft({
      doctor_name: formatDoctorName(sub.doctor_name) || sub.doctor_name || "",
      qualification: sub.qualification || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!draft.doctor_name.trim()) { toast.error("Doctor name cannot be empty"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/submissions/${id}`, draft);
      setSub(data);
      setEditing(false);
      toast.success("Details updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const downloadLama = () => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${id}/lama?auth=${token}`, "_blank");
  };

  const changeStatus = async (status) => {
    const previous = sub.status;
    setSub((s) => ({ ...s, status }));
    try {
      await api.patch(`/admin/submissions/${id}/status`, { status });
      toast.success(`Marked ${status}`);
    } catch {
      setSub((s) => ({ ...s, status: previous }));
      toast.error("Could not update status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0B5351]" />
      </div>
    );
  }

  if (notFound || !sub) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA] px-6">
        <div className="text-center" data-testid="submission-not-found">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="font-heading font-bold text-slate-900 mb-1">Submission not found</p>
          <p className="text-slate-500 text-sm mb-6">It may have been removed.</p>
          <Button onClick={() => navigate("/admin")} className="rounded-xl bg-[#0B5351] hover:bg-[#094241] h-11 px-6">
            Back to submissions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Logo className="h-9 w-9" />
            <div className="min-w-0">
              <p className="font-heading font-bold text-slate-900 leading-none truncate">Submission</p>
              <p className="text-xs text-slate-500 truncate">Blue Cross · World Diabetes Day</p>
            </div>
          </div>
          <Button data-testid="detail-back" onClick={() => navigate("/admin")} variant="outline" size="sm"
            className="rounded-lg h-11 px-3 shrink-0">
            <ArrowLeft className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Clinic Details</p>
            {editing ? (
              <div className="space-y-3 max-w-md">
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400">Doctor's Name</label>
                  <Input data-testid="edit-doctor-name" className="h-11 bg-white" value={draft.doctor_name}
                    onChange={(e) => setDraft({ ...draft, doctor_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400">Qualification</label>
                  <Input data-testid="edit-qualification" className="h-11 bg-white" value={draft.qualification}
                    onChange={(e) => setDraft({ ...draft, qualification: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <Button data-testid="edit-save" onClick={saveEdit} disabled={saving}
                    className="rounded-lg h-11 bg-[#0B5351] hover:bg-[#094241]">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" /> Save</>}
                  </Button>
                  <Button variant="outline" data-testid="edit-cancel" onClick={() => setEditing(false)}
                    className="rounded-lg h-11 border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 flex-wrap">
                <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 break-words"
                  data-testid="detail-doctor-name">
                  {formatDoctorName(sub.doctor_name) || "Submission"}
                </h1>
                <Button variant="outline" size="sm" data-testid="detail-edit-name" onClick={startEdit}
                  className="rounded-lg h-9 px-3 mt-1 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
              </div>
            )}
            <p className="text-slate-500 mt-1">
              Submitted by {sub.mr_name || sub.mr_emp_code} · {formatDate(sub.created_at)}
            </p>
          </div>
          <div className="shrink-0">
            <p className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400 mb-1.5">Status</p>
            <Select value={sub.status || "Pending"} onValueChange={changeStatus}>
              <SelectTrigger data-testid="detail-status-select"
                className={`h-11 w-[150px] font-semibold bg-white ${STATUS_STYLES[sub.status] || STATUS_STYLES.Pending}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((st) => (
                  <SelectItem key={st} value={st} data-testid={`detail-status-${st}`}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-6">
            <Card title="Doctor & Clinic">
              <Row icon={<Stethoscope className="h-3.5 w-3.5" />} label="Doctor's Name" value={formatDoctorName(sub.doctor_name)} />
              <Row icon={<GraduationCap className="h-3.5 w-3.5" />} label="Qualification" value={sub.qualification} />
              <Row icon={<ClipboardList className="h-3.5 w-3.5" />} label="Additional Requirements" value={sub.additional_requirements} />
            </Card>
          </div>

          <div className="space-y-6">
            <Card title="Photo">
              <div className="py-4">
                <Photo label="Doctor Photo" path={sub.doctor_photo} />
              </div>
            </Card>

            <Card title="Standee">
              <div className="py-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400">Status</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STANDEE_STYLES[sub.standee_status] || STANDEE_STYLES.None}`}
                    data-testid="detail-standee-status">
                    {sub.standee_status || "None"}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  {(sub.standee_file || sub.doctor_photo) && (
                    <Button variant="outline" data-testid="detail-standee-download"
                      onClick={downloadStandee}
                      className="rounded-lg h-11 flex-1 border-slate-200 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900">
                      <Download className="h-4 w-4 mr-2" /> Download
                    </Button>
                  )}
                  <Button data-testid="detail-standee-open"
                    onClick={() => navigate(`/admin/submissions/${id}/standee`)}
                    className="rounded-lg h-11 flex-1 bg-[#0B5351] hover:bg-[#094241]">
                    <ImageIcon className="h-4 w-4 mr-2" />
                    {sub.standee_file ? "Edit standee" : "Create standee"}
                  </Button>
                </div>
              </div>
            </Card>

            <Card title="Lama">
              <div className="py-4 space-y-3">
                <p className="text-sm text-slate-500">
                  {sub.lama_saved_at
                    ? `The name board for ${formatDoctorName(sub.doctor_name) || "this doctor"}, as saved in the lama studio.`
                    : sub.standee_photo || sub.doctor_photo
                      ? `The name board for ${formatDoctorName(sub.doctor_name) || "this doctor"}, using the standee's photo. Open the lama studio to adjust it.`
                      : "No doctor photo was submitted; upload one in the lama studio first."}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="outline" data-testid="detail-lama-download"
                    onClick={downloadLama} disabled={!sub.lama_photo && !sub.standee_photo && !sub.doctor_photo}
                    className="rounded-lg h-11 flex-1 border-slate-200 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900">
                    <Download className="h-4 w-4 mr-2" /> Download
                  </Button>
                  <Button data-testid="detail-lama-open"
                    onClick={() => navigate(`/admin/submissions/${id}/lama-studio`)}
                    className="rounded-lg h-11 flex-1 bg-[#0B5351] hover:bg-[#094241]">
                    <ImageIcon className="h-4 w-4 mr-2" />
                    {sub.lama_saved_at ? "Edit lama" : "Lama studio"}
                  </Button>
                </div>
              </div>
            </Card>

            <Card title="Submitted By">
              <Row icon={<User className="h-3.5 w-3.5" />} label="Manager Name" value={sub.manager_name} />
              <Row icon={<Phone className="h-3.5 w-3.5" />} label="Manager Mobile" value={sub.manager_mobile} />
              <Row icon={<Home className="h-3.5 w-3.5" />} label="Manager Address" value={sub.manager_address} />
              <Row icon={<Building2 className="h-3.5 w-3.5" />} label="Head Quarter" value={sub.headquarter} />
              <Row icon={<Hash className="h-3.5 w-3.5" />} label="EMP Code" value={sub.mr_emp_code} mono />
              <Row icon={<CalendarClock className="h-3.5 w-3.5" />} label="Submitted At" value={formatDate(sub.created_at)} />
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
